import { getPreviousConsentForRecipient } from '@lyrahealth-inc/shared-app-logic/src/features/consent/consentUtils'
import { ConsentBannerRecipients } from '@lyrahealth-inc/shared-app-logic/src/features/consent/messages'
import { getDbtTeensSessionAttendeeRoles } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/getDbtTeensSessionAttendeeNames'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  ConsentBannerStatus,
  ConsentStatus,
  ConsentTracking,
  MIXED_CONSENT_BANNER_STATUS,
} from '@lyrahealth-inc/shared-app-logic/src/models/consent/Consent'
import { VideoSession } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

/**
 * Turns the record a pending consent replaced into the banner status to show. No previous record
 * means a first-ever consent (PENDING). A previous record that is still SIGNED means the client has
 * a valid consent on file (e.g. a provider manually assigned a second one), so no banner.
 */
const toBannerStatus = (previousConsent: ConsentTracking | undefined): ConsentBannerStatus | undefined => {
  if (!previousConsent) return ConsentStatus.PENDING
  if (previousConsent.status === ConsentStatus.SIGNED) return undefined
  return previousConsent.status
}

/**
 * Resolves the status that should drive the informed-consent banner for one incomplete care
 * consent assignment.
 *
 * Returns undefined when this assignment shouldn't show a banner — e.g. its own tracking record
 * is no longer PENDING (it was SUPERSEDED and marked missed), or there is no consent data.
 */
export const getConsentBannerStatusForAssignment = (
  consents: ConsentTracking[] | undefined,
  assignmentId: string,
  recipientLyraId: string | undefined,
): ConsentBannerStatus | undefined => {
  if (!consents?.length || !recipientLyraId) return undefined

  const ownConsent = consents.find((consent) => consent.assignment_id === assignmentId)
  if (!ownConsent || ownConsent.status !== ConsentStatus.PENDING) return undefined

  return toBannerStatus(
    getPreviousConsentForRecipient({ consents, recipientLyraId, excludeAssignmentId: assignmentId }),
  )
}

/**
 * Resolves the status that should drive the in-session informed-consent banner for a recipient,
 * independent of any particular assignment (the video session isn't scoped to one).
 *
 * A banner is warranted only while the recipient has an outstanding PENDING consent. The status
 * shown is that of their latest non-pending record (EXPIRING_SOON / EXPIRED / SUPERSEDED), which
 * explains why re-signing is needed; a first-ever consent with no history resolves to PENDING.
 *
 * Returns undefined when no banner is warranted: the recipient has nothing pending, or their latest
 * record is still SIGNED (a valid consent is on file).
 */
export const getConsentBannerStatusForRecipient = (
  consents: ConsentTracking[] | undefined,
  recipientLyraId: string | undefined,
): ConsentBannerStatus | undefined => {
  if (!consents?.length || !recipientLyraId) return undefined

  const hasPendingConsent = consents.some(
    (consent) => consent.recipient_id === recipientLyraId && consent.status === ConsentStatus.PENDING,
  )
  if (!hasPendingConsent) return undefined

  return toBannerStatus(getPreviousConsentForRecipient({ consents, recipientLyraId }))
}

export type SessionConsentRecipients = {
  client?: { lyraId: string; firstName?: string; pronouns?: string[] }
  caregiver?: { lyraId: string; firstName?: string }
}

/**
 * Who is in a video session, and so whose consents the in-session banner can be about. Uses the
 * same DBT for Teens attendee rules as the session labels (caregiver-only sessions, teen-only
 * follow-ups, and intake / family / medication / first sessions with both); any other session is
 * with the episode's patient.
 */
export const getConsentRecipientsForSession = (
  appointment:
    | Pick<
        Appointment,
        'programId' | 'sessionNumber' | 'sessionType' | 'guardianLyraId' | 'guardianFirstName' | 'userInfo'
      >
    | null
    | undefined,
  videoSession: Pick<VideoSession, 'patient_id'> | null | undefined,
  clientPronouns?: string[],
): SessionConsentRecipients => {
  const client = videoSession?.patient_id
    ? { lyraId: videoSession.patient_id, firstName: appointment?.userInfo?.firstName, pronouns: clientPronouns }
    : undefined
  const caregiver = appointment?.guardianLyraId
    ? { lyraId: appointment.guardianLyraId, firstName: appointment.guardianFirstName }
    : undefined

  const attendeeRoles = appointment ? getDbtTeensSessionAttendeeRoles(appointment) : undefined
  if (!attendeeRoles || !caregiver) {
    return { client }
  }

  return {
    ...(attendeeRoles.includes('teen') ? { client } : {}),
    ...(attendeeRoles.includes('caregiver') ? { caregiver } : {}),
  }
}

export type SessionInformedConsentBanner = {
  status: ConsentBannerStatus
  recipients: ConsentBannerRecipients
}

/**
 * Resolves the in-session informed-consent banner for everyone in the session: which status to
 * explain and who still has to sign, so the copy can name the client, the caregiver, or both.
 * Returns undefined when nobody in the session has an outstanding consent.
 *
 * When both need to sign for different reasons (e.g. the teen's form expired but the caregiver's is
 * only expiring soon), neither reason is true of both, so the MIXED copy covering all reasons is used.
 */
export const getInformedConsentBannerForSession = (
  consents: ConsentTracking[] | undefined,
  recipients: SessionConsentRecipients,
): SessionInformedConsentBanner | undefined => {
  const clientStatus = recipients.client
    ? getConsentBannerStatusForRecipient(consents, recipients.client.lyraId)
    : undefined
  const caregiverStatus = recipients.caregiver
    ? getConsentBannerStatusForRecipient(consents, recipients.caregiver.lyraId)
    : undefined

  if (clientStatus && caregiverStatus) {
    return {
      status: clientStatus === caregiverStatus ? clientStatus : MIXED_CONSENT_BANNER_STATUS,
      recipients: { client: recipients.client, caregiver: recipients.caregiver },
    }
  }
  if (clientStatus) {
    return { status: clientStatus, recipients: { client: recipients.client } }
  }
  if (caregiverStatus) {
    return { status: caregiverStatus, recipients: { caregiver: recipients.caregiver } }
  }
  return undefined
}
